import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Upload,
  Trash2,
  Loader2,
  CheckCircle2,
  FileSpreadsheet,
  Info,
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  ListChecks,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { chartAbstractionCache, fetchChartAbstractionDocument } from "@/lib/admin-cache";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ChartAbstractionDocument {
  id: number;
  questionsData: Record<string, string[]>;
  originalFilename: string;
  measureCount: number;
  maxQuestions: number;
  uploadedAt: string;
  uploadedBy: {
    id: number;
    name: string;
    email: string;
  };
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export function ChartAbstractionQuestionsConfig() {
  const { toast } = useToast();
  const { user, token } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Stale-while-revalidate: initialize from the login-time prefetch cache so
  // the tab renders instantly; the effect below still refreshes in background.
  const [document, setDocument] = useState<ChartAbstractionDocument | null>(
    chartAbstractionCache.document
  );
  const [loading, setLoading] = useState(!chartAbstractionCache.loaded);
  const [uploading, setUploading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState<string | null>(
    chartAbstractionCache.document
      ? Object.keys(chartAbstractionCache.document.questionsData).sort()[0] ?? null
      : null
  );
  const [search, setSearch] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchDocument();
  }, [token]);

  const fetchDocument = async () => {
    if (!token) return;
    if (!chartAbstractionCache.loaded) setLoading(true);
    try {
      const doc: ChartAbstractionDocument | null =
        await fetchChartAbstractionDocument(token);
      // Bail out when nothing changed — this runs as a BACKGROUND refresh
      // after a cache-seeded render, and swapping in an identical new object
      // re-renders (and jumps) the whole explorer for no reason.
      setDocument(prev =>
        prev && doc && prev.id === doc.id && prev.uploadedAt === doc.uploadedAt
          ? prev
          : doc
      );
      // Keep the user's selection when it still exists in the (possibly new)
      // document; only auto-select the first measure when it doesn't.
      setSelectedMeasure(prev =>
        doc
          ? prev && doc.questionsData[prev]
            ? prev
            : Object.keys(doc.questionsData).sort()[0] ?? null
          : null
      );
    } catch (error) {
      console.error('Error fetching chart abstraction questions:', error);
      toast({
        title: "Error",
        description: "Failed to load Chart Abstraction Questions document",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({ title: "Invalid File Type", description: "Please upload a CSV file", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const csvContent = await file.text();
      const response = await fetch(`${API_URL}/api/chart-abstraction-questions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ csvContent, filename: file.name }),
      });
      const data = await response.json();
      if (response.ok) {
        toast({
          title: "Upload Successful",
          description: `Loaded ${data.document.measureCount} measures with up to ${data.document.maxQuestions} questions each`,
        });
        fetchDocument();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload document",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!document) return;
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/chart-abstraction-questions/${document.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Delete failed');
      toast({ title: "Document Deleted", description: "Chart Abstraction Questions document has been deleted" });
      setDocument(null);
      setSelectedMeasure(null);
      chartAbstractionCache.document = null;
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({ title: "Delete Failed", description: "Failed to delete document", variant: "destructive" });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Rebuild a CSV from the parsed questions map. `document` is state, so DOM uses window.document.
  const downloadCsv = () => {
    if (!document) return;
    const data = document.questionsData;
    const measureIds = Object.keys(data).sort();
    const maxRows = measureIds.reduce((m, id) => Math.max(m, data[id]?.length || 0), 0);
    const esc = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines: string[] = [measureIds.map(id => `M${id}`).join(',')];
    for (let r = 0; r < maxRows; r++) {
      lines.push(measureIds.map(id => esc(data[id]?.[r] || '')).join(','));
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = document.originalFilename || 'chart_abstraction_questions.csv';
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const measureIds = document ? Object.keys(document.questionsData).sort() : [];
  const searchKey = search.trim().toLowerCase().replace(/^m/, '');
  const filteredMeasures = measureIds.filter(id => id.toLowerCase().includes(searchKey));
  const selectedQuestions = selectedMeasure ? (document?.questionsData[selectedMeasure] || []) : [];

  // Plain JSX (NOT an inline component): defining a component inside render
  // gives React a new type each render, which unmounts/remounts this section
  // on every state change — a visible flicker when background refreshes land.
  const formatHelp = (
    <div className="rounded-lg border">
      <button
        type="button"
        onClick={() => setHelpOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        {helpOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Info className="h-4 w-4 text-blue-600" />
        CSV format requirements
      </button>
      {helpOpen && (
        <div className="px-4 pb-4 space-y-2 text-sm text-muted-foreground">
          <ul className="space-y-1 ml-1 list-disc list-inside">
            <li>One column per measure; headers match <code className="bg-muted px-1 rounded">M###</code> (e.g. M001, M047, M326).</li>
            <li>Row 1 = measure IDs (headers). Rows 2+ = one question per row.</li>
            <li>Leave a cell blank when a measure has fewer questions.</li>
          </ul>
          <div className="font-mono text-xs bg-muted p-2 rounded overflow-x-auto whitespace-pre">
{`M001,M047
"Enter the patient's most recent HbA1c value and date drawn.","Was an advance care plan discussed and documented?"
"If no HbA1c was recorded this period, document the reason.","If not documented, document the reason."`}
          </div>
        </div>
      )}
    </div>
  );

  // Always render the SAME card chrome. Returning a short spinner card while
  // loading (then swapping to the full explorer) is what made the page height
  // collapse for a frame → scrollbar pop → whole Admin layout jump.
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Chart Abstraction Questions
        </CardTitle>
        <CardDescription>
          The reference document mapping quality measures to their abstraction questions. Used when generating
          Chart Abstraction Lists for practices.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5 min-h-[560px]">
        <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileSelect} />

        {loading ? (
          <div className="flex justify-center items-center min-h-[480px]">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !document ? (
          /* Empty state */
          <div className="space-y-4">
            <div className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-center py-12 px-6">
              <div className="p-3 rounded-full bg-muted mb-3">
                <FileSpreadsheet className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="font-medium">No questions document uploaded</p>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Practices can't generate Chart Abstraction Lists until a questions document is uploaded.
              </p>
              {isAdmin && (
                <Button className="mt-4" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {uploading ? 'Uploading…' : 'Upload CSV'}
                </Button>
              )}
            </div>
            {formatHelp}
          </div>
        ) : (
          <>
            {/* Summary bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{document.originalFilename}</p>
                  <p className="text-xs text-muted-foreground">
                    Updated {formatDate(document.uploadedAt)} · {document.uploadedBy?.name || document.uploadedBy?.email || 'Unknown'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-lg font-semibold leading-none">{document.measureCount}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Measures</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold leading-none">{document.maxQuestions}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Max Q&apos;s</p>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2 pl-2 border-l">
                    <Button variant="outline" size="sm" onClick={downloadCsv} title="Download CSV">
                      <Download className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Download</span>
                    </Button>
                    <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Replace document">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin sm:mr-2" /> : <Upload className="h-4 w-4 sm:mr-2" />}
                      <span className="hidden sm:inline">{uploading ? 'Uploading…' : 'Replace'}</span>
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setShowDeleteDialog(true)} disabled={deleting} title="Delete document">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Two-pane explorer */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Measure list */}
              <div className="rounded-lg border overflow-hidden flex flex-col">
                <div className="p-2 border-b bg-muted/40">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search measures…"
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                  {filteredMeasures.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No measures match.</p>
                  ) : (
                    filteredMeasures.map(id => {
                      const count = document.questionsData[id]?.length || 0;
                      const active = id === selectedMeasure;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setSelectedMeasure(id)}
                          className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left border-l-2 transition-colors ${
                            active
                              ? 'bg-primary/10 border-primary text-primary font-medium'
                              : 'border-transparent hover:bg-muted'
                          }`}
                        >
                          <span>M{id}</span>
                          <span className={`text-xs ${active ? 'text-primary' : 'text-muted-foreground'}`}>{count}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Question preview */}
              <div className="md:col-span-2 rounded-lg border p-4 min-h-[200px]">
                {selectedMeasure ? (
                  <>
                    <div className="flex items-center gap-2 pb-3 mb-3 border-b">
                      <ListChecks className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">Measure M{selectedMeasure}</span>
                      <span className="text-sm text-muted-foreground">
                        · {selectedQuestions.length} question{selectedQuestions.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {selectedQuestions.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No questions defined for this measure.</p>
                    ) : (
                      <ol className="space-y-2">
                        {selectedQuestions.map((q, i) => (
                          <li key={i} className="flex gap-3 text-sm">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                              {i + 1}
                            </span>
                            <span className="pt-0.5">{q}</span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-8">
                    <ListChecks className="h-6 w-6 mb-2 opacity-40" />
                    <p className="text-sm">Select a measure to preview its questions</p>
                  </div>
                )}
              </div>
            </div>

            {formatHelp}
          </>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Chart Abstraction Questions Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Practices won't be able to generate Chart Abstraction Lists until a new
              document is uploaded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</> : 'Delete Document'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
